import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { generateCaseNumber } from '../utils/helpers';
import toast from 'react-hot-toast';
import './NewCase.css';

export default function NewCase() {
  const navigate = useNavigate();
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyForOptions, setApplyForOptions] = useState([]);

  useEffect(() => {
    const loadApplyForOptions = async () => {
      try {
        const data = await api.applyForOptions.active();
        setApplyForOptions(data);
      } catch (err) {
        console.error('Failed to load apply for options');
      }
    };
    loadApplyForOptions();
  }, []);

  const [formData, setFormData] = useState({
    university_id: '',
    applicant_name: '',
    college_name: '',
    apply_for: '',
    apply_date: '',
    delivery_type: 'email',
    delivery_address: '',
  });
  const [documents, setDocuments] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [caseNumber, setCaseNumber] = useState('');
  const [createdCaseId, setCreatedCaseId] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.universities.list();
        setUniversities(Array.isArray(data) ? data : (data.universities || data.data || []));
      } catch (e) {
        toast.error('Failed to load universities');
      }
    };
    load();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFiles = (e) => {
    setDocuments(prev => [...prev, ...Array.from(e.target.files)]);
  };

  const removeFile = (i) => {
    setDocuments(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleSubmit = async () => {
    if (!formData.university_id) { toast.error('Please select a university'); return; }
    if (!formData.applicant_name?.trim()) { toast.error('Please enter student name'); return; }
    if (!formData.college_name?.trim()) { toast.error('Please enter college name'); return; }
    if (!formData.apply_for) { toast.error('Please select what they applied for'); return; }

    setLoading(true);
    try {
      const newCaseNumber = generateCaseNumber();
      const response = await api.cases.create({ case_number: newCaseNumber, ...formData });
      const caseId = response.data?.id || response.id;

      if (documents.length > 0 && caseId) {
        const fd = new FormData();
        documents.forEach(doc => fd.append('documents', doc));
        try { await api.documents.upload(caseId, fd); } catch (e) {
          toast.warning('Case created but some documents failed to upload');
        }
      }

      setCaseNumber(newCaseNumber);
      setCreatedCaseId(caseId);
      setShowSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create case');
    } finally {
      setLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="new-case-page">
        <div className="success-card">
          <div className="success-icon">&#10003;</div>
          <h2>Case Created Successfully</h2>
          <p>Your new case has been created and the university has been notified.</p>
          <div className="case-num-box">
            <span className="case-num-label">Case Number</span>
            <span className="case-num-value">{caseNumber}</span>
          </div>
          <button className="btn btn-primary" onClick={() => createdCaseId ? navigate(`/cases/${createdCaseId}`) : navigate('/cases')} style={{width:'100%',marginTop:'1.5rem'}}>
            View Case
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="new-case-page">
      <div className="page-header">
        <h1>Create New Case</h1>
      </div>

      <div className="form-card">
        <div className="form-group">
          <label className="form-label">University *</label>
          <select name="university_id" value={formData.university_id} onChange={handleChange} className="form-select">
            <option value="">Select University</option>
            {universities.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Student Name *</label>
          <input type="text" name="applicant_name" value={formData.applicant_name} onChange={handleChange} className="form-input" placeholder="Enter student name" />
        </div>

        <div className="form-group">
          <label className="form-label">College Name *</label>
          <input type="text" name="college_name" value={formData.college_name} onChange={handleChange} className="form-input" placeholder="Enter college name" />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Applied For *</label>
            <select name="apply_for" value={formData.apply_for} onChange={handleChange} className="form-select">
              {applyForOptions.length === 0 ? (
                <option value="">Loading...</option>
              ) : (
                <>
                  <option value="">Select type</option>
                  {applyForOptions.map(opt => (
                    <option key={opt.id} value={opt.name}>{opt.name}</option>
                  ))}
                </>
              )}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Apply Date</label>
            <input type="date" name="apply_date" value={formData.apply_date} onChange={handleChange} className="form-input" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Delivery To</label>
          <div className="delivery-options">
            <label className="radio-label">
              <input type="radio" name="delivery_type" value="email" checked={formData.delivery_type === 'email'} onChange={handleChange} />
              <span>Email ID</span>
            </label>
            <label className="radio-label">
              <input type="radio" name="delivery_type" value="address" checked={formData.delivery_type === 'address'} onChange={handleChange} />
              <span>Physical Address</span>
            </label>
          </div>
          {formData.delivery_type === 'email' ? (
            <input type="email" name="delivery_address" value={formData.delivery_address} onChange={handleChange} className="form-input" placeholder="Enter email address" />
          ) : (
            <textarea name="delivery_address" value={formData.delivery_address} onChange={handleChange} className="form-textarea" placeholder="Enter full delivery address" rows={3} />
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Upload Documents</label>
          <div className="upload-area">
            <input type="file" multiple onChange={handleFiles} className="file-input" id="doc-upload" />
            <label htmlFor="doc-upload" className="upload-label">
              <span className="upload-icon">&#8593;</span>
              <span>Click to browse or drag files here</span>
            </label>
          </div>
          {documents.length > 0 && (
            <div className="file-list">
              {documents.map((doc, i) => (
                <div key={i} className="file-item">
                  <span>{doc.name}</span>
                  <button type="button" onClick={() => removeFile(i)} className="remove-file">&times;</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button onClick={() => navigate('/cases')} className="btn btn-secondary">Cancel</button>
          <button onClick={handleSubmit} className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Case & Notify University'}
          </button>
        </div>
      </div>
    </div>
  );
}
